import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { Mail, RotateCw } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { useAuthStore } from '../../stores/authStore'
import { useToast } from '../../stores/toastStore'
import { Button } from '../../components/ui/Button'
import type { RispostaApi, Utente } from '../../lib/tipi'
import styles from './Auth.module.css'

export function Accedi() {
  const navigate = useNavigate()
  const entra = useAuthStore((s) => s.entra)
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)
  // Email da verificare: settata quando il login ritorna 403 (account
  // non attivo / email non confermata). Mostra un blocco dedicato sotto
  // il form con il pulsante "Reinvia email di conferma".
  const [emailDaVerificare, setEmailDaVerificare] = useState<string | null>(null)
  const [reinvioInCorso, setReinvioInCorso] = useState(false)
  // Countdown anti-spam dopo il reinvio (60s). Backend ha già rate limit
  // 3/10min, questo è feedback UX immediato.
  const [secondiAttesa, setSecondiAttesa] = useState(0)

  // Messaggi di stato dopo flussi esterni (es. reset password completato,
  // conferma email da link Supabase).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const messaggio = params.get('messaggio')
    if (messaggio === 'password_aggiornata') {
      toast.successo('Password aggiornata con successo. Accedi con la nuova password.')
      window.history.replaceState({}, '', '/accedi')
    }
    if (messaggio === 'email_confermata') {
      toast.successo('Email confermata! Accedi per iniziare ad allenarti.')
      window.history.replaceState({}, '', '/accedi')
    }
  // eseguito solo al mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Countdown del bottone "Reinvia"
  useEffect(() => {
    if (secondiAttesa <= 0) return
    const id = setTimeout(() => setSecondiAttesa((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [secondiAttesa])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErrore(null)
    setEmailDaVerificare(null)
    setCaricamento(true)
    try {
      const res = await api.post<RispostaApi<{ token: string; utente: Utente }>>(
        '/auth/accedi',
        { email, password },
      )
      const dati = res.data.dati
      if (!dati) throw new Error('Risposta vuota')

      entra(dati.token, dati.utente)
      // Redirect in base al ruolo
      const ruolo = dati.utente.ruolo
      navigate(
        ruolo === 'superadmin' ? '/admin/dashboard'
        : ruolo === 'manager' ? '/manager/dashboard'
        : '/app/dashboard',
        { replace: true },
      )
    } catch (err) {
      // Status 403 = account non attivo (email non verificata). Lo
      // distinguiamo dal generico 401 "credenziali non valide" per
      // mostrare il blocco con il pulsante "Reinvia email di conferma".
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setEmailDaVerificare(email)
        setErrore(null)
      } else {
        setErrore(messaggioErrore(err))
      }
    } finally {
      setCaricamento(false)
    }
  }

  const reinviaEmail = async (): Promise<void> => {
    if (!emailDaVerificare || reinvioInCorso || secondiAttesa > 0) return
    setReinvioInCorso(true)
    try {
      // Backend risponde sempre con successo (no enumeration). Il campo
      // `tipo` distingue tra due esiti:
      //  - 'attivato_direttamente' → l'account era già confermato in
      //    Supabase Auth (caso edge), il DB è stato allineato; l'utente
      //    può fare login subito senza aspettare email.
      //  - 'email_inviata' (o assente) → email partita, countdown anti-spam.
      const res = await api.post<{ dati: { messaggio: string; tipo?: string } }>(
        '/auth/reinvia-verifica',
        { email: emailDaVerificare },
      )
      const dati = res.data.dati
      const messaggio = dati?.messaggio ?? 'Operazione completata.'

      if (dati?.tipo === 'attivato_direttamente') {
        toast.successo(messaggio)
        // Nascondi il blocco arancione: l'utente può accedere subito
        setEmailDaVerificare(null)
      } else {
        toast.successo(messaggio)
        setSecondiAttesa(60)
      }
    } catch (err) {
      toast.errore(messaggioErrore(err))
    } finally {
      setReinvioInCorso(false)
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logoBox}>RC</div>
          <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
        </div>

        <h1 className={styles.titolo}>Accedi</h1>
        <p className={styles.descrizione}>
          Inserisci email e password per entrare nel tuo account.
        </p>

        <form onSubmit={submit} className={styles.form}>
          <label className={styles.campo}>
            <span className={styles.lbl}>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder="mario@esempio.it"
            />
          </label>

          <label className={styles.campo}>
            <span className={styles.lbl}>Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="••••••••"
            />
          </label>

          {errore && <div className={styles.errore}>{errore}</div>}

          {emailDaVerificare && (
            <div style={{
              padding: '12px 14px',
              background: 'var(--warning-soft)',
              border: '1px solid var(--warning)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: 'var(--warning)',
                fontWeight: 600,
                fontSize: 13,
              }}>
                <Mail size={16} />
                Email non ancora verificata
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Controlla la tua casella di posta (anche lo <strong>spam</strong>).
                Se non trovi l'email, possiamo rimandartela.
              </div>
              <Button
                type="button"
                variante="secondary"
                taglia="sm"
                iconaSinistra={<RotateCw size={13} />}
                onClick={reinviaEmail}
                caricamento={reinvioInCorso}
                disabled={reinvioInCorso || secondiAttesa > 0}
                style={{ alignSelf: 'flex-start' }}
              >
                {secondiAttesa > 0
                  ? `Reinvia tra ${secondiAttesa}s`
                  : 'Reinvia email di conferma'}
              </Button>
            </div>
          )}

          <Button type="submit" taglia="lg" caricamento={caricamento}>
            Entra
          </Button>
        </form>

        <div className={styles.linkRiga}>
          <Link to="/auth/password-dimenticata" className={styles.link}>
            Password dimenticata?
          </Link>
        </div>

        <div className={styles.linkRiga} style={{ marginTop: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Nuovo su RingCoach?{' '}
            <Link to="/registrati" className={styles.link}>Crea un account</Link>
          </span>
        </div>
      </div>
    </div>
  )
}
